/**
 * @preserve
 * Licenses of bundled dependencies can be found in dependencies-licenses.txt
 */

import { r as registerInstance, d as createEvent, h, c as Host, g as getElement } from './index-B0vaMlyg.js';
import { t as transformTagNames } from './transform-tag-name-B5E60fj5.js';
import { a as SharedDirectionService } from './init-services-CKZT6dhM.js';
import { D as DebounceDelays } from './debounce-constants-DjdUiR4p.js';
import { g as getDataOnFromTheme } from './themes-DVWPp0eL.js';
import { i as isElementInViewport } from './is-element-in-viewport-DRGGMLzn.js';
import { q as querySelectorAllAsArray } from './query-selector-all-as-array-BMNvfiXu.js';
import { t as throttle } from './throttle-D1sPO_YK.js';
import './direction.service-g9OBd7A0.js';

const NAVIGATION_BEHAVIOUR_PAGE = 'page';
const NAVIGATION_BEHAVIOUR_ITEM = 'item';
const SCROLL_BEHAVIOUR_SMOOTH = 'smooth';

const sliderCss = ":host{--dot-nav-offset:var(--wb-slider-gap);display:block;position:relative;overflow:hidden}.outer{position:relative}.inner{position:relative;z-index:1;display:flex;flex-direction:row;justify-content:flex-start;flex-wrap:nowrap;overflow-y:hidden;overflow-x:auto;flex-shrink:0;flex-grow:0;-webkit-overflow-scrolling:touch;scrollbar-width:none}.inner::-webkit-scrollbar{display:none}.arrow{position:absolute;z-index:2;inset-block-start:50%;transform:translateY(-50%)}.arrow--previous{inset-inline-start:1rem}.arrow--next{inset-inline-end:1rem}.wb-dot-navigation{margin-block-start:var(--dot-nav-offset)}.grid-width{width:var(--wb-grid-width);margin:0 auto}:host([dot-navigation-position=bottom]) .wb-dot-navigation{position:absolute;inset-inline:50%;inset-block-end:var(--dot-nav-offset);z-index:1}:host([grid-gutter]) ::slotted(.wb-slider-item:not(:first-of-type)){margin-inline-start:var(--wb-grid-gutter-width)}:host([off-grid]) .inner{--offgrid-spacing:calc((100% - var(--wb-grid-width)) / 2)}:host([off-grid]) .inner::before{content:\"\";flex:0 0 var(--offgrid-spacing)}:host([off-grid]) .inner::after{flex:0 0 calc(var(--scroll-spacing))}@media (max-width: 47.99rem){:host([off-grid]) .arrow--previous{inset-inline-start:calc(50% - var(--wb-grid-width) / 2 - 0.8rem)}:host([off-grid]) .arrow--next{inset-inline-start:unset;inset-inline-end:calc(50% - var(--wb-grid-width) / 2 - 0.8rem)}}@media (min-width: 48rem){:host([off-grid]) .arrow{transform:translate3d(-50%, -50%, 0)}:host([off-grid]) .arrow--previous{inset-inline-start:calc(50% - var(--wb-grid-width) / 2 - var(--wb-grid-gutter-width))}:host([off-grid]) .arrow--next{inset-inline-start:calc(50% + var(--wb-grid-width) / 2 + var(--wb-grid-gutter-width));inset-inline-end:initial}}:host([off-grid][scroll-snap]) .inner{scroll-padding-inline-start:calc((100% - var(--wb-grid-width)) / 2)}:host([scroll-snap]) .inner{scroll-snap-type:x mandatory}:host([scroll-snap]) ::slotted(.wb-slider-item){scroll-snap-align:start}@media (min-width: 48rem){:host([is-rtl][off-grid]) .arrow{transform:translate3d(50%, -50%, 0)}}";

const MAGIC_THRESHOLD_PX = 5;
const SliderComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
        this.wbchange = createEvent(this, "wbchange", 7);
        /** shows an off-grid variant when the components goes over the full width of the page  */
        this.offGrid = false;
        /** adds a grid gutter between the slider items */
        this.gridGutter = false;
        /** enables scroll snapping */
        this.scrollSnap = false;
        /** sets navigation behaviour scrolling by page or item */
        this.navigationBehaviour = NAVIGATION_BEHAVIOUR_PAGE;
        /** shows the arrow navigation */
        this.showArrowNavigation = false;
        /** shows the dot navigation */
        this.showDotNavigation = false;
        /** interval in miliseconds to auto rotate slides. pass a falsy value to stop auto rotation */
        this.autoRotateIntervalMs = 0;
        /** sets item to jump to without smooth scrolling. can be updated during runtime */
        this.initialItem = 1;
        /** defines position of dot navigation, if rendered */
        this.dotNavigationPosition = 'below';
        /** defines if dot navigation should have background */
        this.dotNavigationHasBackground = false;
        /** @internal */
        this.isRtl = false;
        this.scrolledToStart = true;
        this.scrolledToEnd = false;
        this.items = [];
        this.groupSize = 0;
        this.groupedIndexes = [];
        this.numberOfDots = 0;
        this.currentItem = 1;
        this.edges = { right: 0, left: 0 };
        this.isLoaded = false;
        this.TagNames = transformTagNames(this, 'wb-slider', ['wb-icon', 'wb-round-button', 'wb-dot-navigation', 'wb-slider-item']);
        this.isUserHovering = false; // Prevents auto rotation when user hovers over the slider
        this.throttledUpdate = throttle(() => this.update(), DebounceDelays.Scroll);
        this.setLoaded = () => {
            // Set isLoaded to ensure the edges are calculated after the items are rendered
            requestAnimationFrame(() => {
                this.isLoaded = true;
            });
        };
        this.scrollToPage = (previous = false) => {
            const refEl = this.offGrid ? this.gridWidthEl : this.el;
            let scrollDist = this.isRtl ? -refEl.clientWidth : refEl.clientWidth;
            if (previous) {
                scrollDist = -scrollDist;
            }
            this.scrollerEl.scroll({ left: this.scrollerEl.scrollLeft + scrollDist, behavior: 'smooth' });
        };
        this.firstFromGroup = (count) => {
            const index = count - 1;
            return this.groupedIndexes[index][0];
        };
        this.onDotNavigationChange = (event) => {
            event.stopPropagation();
            const item = this.firstFromGroup(event.detail);
            this.scrollToItem(item);
        };
        this.onNextButtonClick = () => {
            if (this.navigationBehaviour === NAVIGATION_BEHAVIOUR_ITEM) {
                this.next();
            }
            else {
                this.scrollToNextPage();
            }
        };
        this.onPreviousButtonClick = () => {
            if (this.navigationBehaviour === NAVIGATION_BEHAVIOUR_ITEM) {
                this.previous();
            }
            else {
                this.scrollToPreviousPage();
            }
        };
        this.autoRotateFn = () => {
            if (isElementInViewport(this.el) === false) {
                return;
            }
            if (this.scrolledToEnd) {
                this.scrollToIndex(0);
            }
            else {
                this.next();
            }
        };
        this.updateArrows = () => {
            this.updateScrolledToStart();
            this.updateScrolledToEnd();
        };
        this.updateScrolledToStart = () => {
            const direction = this.isRtl ? -1 : 1;
            const scrollLeft = this.scrollerEl.scrollLeft * direction;
            this.scrolledToStart = scrollLeft < MAGIC_THRESHOLD_PX;
        };
        this.updateScrolledToEnd = () => {
            const direction = this.isRtl ? -1 : 1;
            const scrollLeft = this.scrollerEl.scrollLeft * direction;
            const scrollWidth = this.scrollerEl.scrollWidth;
            this.scrolledToEnd = scrollLeft + this.el.clientWidth + MAGIC_THRESHOLD_PX > scrollWidth;
        };
        this.updateItems = () => {
            this.items = querySelectorAllAsArray(this.el, this.TagNames.WbSliderItem);
        };
        this.bindScrollerRef = (el) => {
            this.scrollerEl = el;
        };
        this.bindGridWidthRef = (el) => {
            this.gridWidthEl = el;
        };
    }
    onMouseEnter() {
        this.isUserHovering = true;
        this.pauseRotation();
    }
    onMouseLeave() {
        this.isUserHovering = false;
        this.continueRotation();
    }
    updateDirection() {
        this.isRtl = SharedDirectionService.isRtl;
    }
    /** returns the number of the currently active slider item (starts at 1) */
    async getCurrentItem() {
        return this.currentItem;
    }
    /** scrolls to the slider item at the given number (starts at 1) */
    async scrollToItem(item, behavior = SCROLL_BEHAVIOUR_SMOOTH) {
        await this.scrollToIndex(item - 1, behavior);
    }
    /** scrolls to the slider item at the given index (starts at 0) */
    async scrollToIndex(index, behavior = SCROLL_BEHAVIOUR_SMOOTH) {
        if (!this.items[index]) {
            return;
        }
        const offsetLeft = this.getItemOffsetStartAtIndex(index);
        const offgridAdjustment = this.offGrid ? this.offsetToGridContentAreaOnEachSide : 0;
        const adjustedOffsetLeft = this.isRtl ? offsetLeft + offgridAdjustment : offsetLeft - offgridAdjustment;
        this.scrollerEl.scroll({ left: adjustedOffsetLeft, behavior });
    }
    /** scrolls to the next slider item */
    async next() {
        const nextItemIndex = Math.min(this.currentItem, this.items.length - 1);
        await this.scrollToIndex(nextItemIndex);
    }
    /** scrolls to the previous slider item */
    async previous() {
        const previousItemIndex = Math.max(this.currentItem - 2, 0);
        await this.scrollToIndex(previousItemIndex);
    }
    /** enforces an update and rerender of the slider */
    async update() {
        this.updateArrows();
        this.updateEdges();
        this.updateCurrentItem();
    }
    /** scrolls to the next slider page */
    async scrollToNextPage() {
        this.scrollToPage();
    }
    /** scrolls to the previous slider page */
    async scrollToPreviousPage() {
        this.scrollToPage(true);
    }
    updateAutoRotation() {
        this.pauseRotation();
        this.continueRotation();
    }
    updateInitialItem() {
        // Wait for next render cycle, so that child elements can finish rendering before scrolling to initial item
        requestAnimationFrame(() => {
            this.scrollToItem(this.initialItem, 'auto');
        });
    }
    updateEdges() {
        if (this.isLoaded) {
            this.getEdges();
        }
    }
    groupItems() {
        if (!this.groupSize) {
            this.getGroupSize();
        }
        if (this.groupSize) {
            this.groupItemsIndexes();
        }
    }
    updateNumberOfDots() {
        this.numberOfDots = Math.ceil(this.items.length / this.groupSize);
    }
    updateCurrentItem() {
        const previousCurrentItem = this.currentItem;
        const lastVisibleItem = this.getFirstVisibleItem(this.items);
        const newCurrentItem = this.items.indexOf(lastVisibleItem) + 1;
        if (previousCurrentItem !== newCurrentItem) {
            this.currentItem = newCurrentItem;
            this.wbchange.emit(this.currentItem);
        }
    }
    connectedCallback() {
        this.updateDirection();
    }
    componentDidLoad() {
        this.updateInitialItem();
        this.updateScrolledToStart();
        this.updateAutoRotation();
        this.setLoaded();
        this.resizeObserver = new ResizeObserver(() => {
            this.throttledUpdate();
        });
        this.resizeObserver.observe(this.scrollerEl);
    }
    componentDidUpdate() {
        this.updateScrolledToEnd();
    }
    getCurrentPage() {
        // If scrolled to the end, return the last page number
        if (this.scrolledToEnd) {
            return this.numberOfDots;
        }
        // If scrolled to the start, return the first page number
        if (this.scrolledToStart) {
            return 1;
        }
        // Find the index of the group that contains the current item
        const groupIndex = this.groupedIndexes.findIndex((group) => group.includes(this.currentItem));
        // If the current item is not part of any group, return -1 (error case)
        if (groupIndex === -1) {
            return -1;
        }
        // If the current item is not the first in its group, adjust the page index
        const isPartialGroup = (this.currentItem - 1) % this.groupSize !== 0;
        return isPartialGroup ? groupIndex + 2 : groupIndex + 1;
    }
    pauseRotation() {
        if (this.autoRotateInterval) {
            clearInterval(this.autoRotateInterval);
        }
    }
    continueRotation() {
        if (!this.autoRotateIntervalMs || this.isUserHovering) {
            return;
        }
        this.autoRotateInterval = setInterval(this.autoRotateFn, this.autoRotateIntervalMs);
    }
    get offsetToGridContentAreaOnEachSide() {
        return (this.el.clientWidth - this.gridWidthEl.clientWidth) / 2;
    }
    getItemOffsetStartAtIndex(index) {
        const hostElementRect = this.el.getBoundingClientRect();
        const hostElementStartEdge = this.isRtl ? hostElementRect.x + hostElementRect.width : hostElementRect.x;
        const itemRect = this.items[index].getBoundingClientRect();
        const itemStartEdge = this.isRtl ? itemRect.x + itemRect.width : itemRect.x;
        return this.scrollerEl.scrollLeft + itemStartEdge - hostElementStartEdge;
    }
    getEdges() {
        const refEl = this.offGrid ? this.gridWidthEl : this.el;
        const coords = refEl.getBoundingClientRect();
        this.edges = {
            right: coords.x + coords.width,
            left: coords.x,
        };
    }
    getFirstVisibleItem(items) {
        return items
            .concat()
            .reverse()
            .find((item) => {
            const coords = item.getBoundingClientRect();
            const itemMiddle = coords.x + coords.width / 2 + MAGIC_THRESHOLD_PX;
            const lastArea = this.edges.right - coords.width;
            const initialArea = this.edges.left + coords.width;
            return this.isRtl ? itemMiddle >= lastArea : itemMiddle <= initialArea;
        });
    }
    getGroupSize() {
        if (this.navigationBehaviour === NAVIGATION_BEHAVIOUR_ITEM) {
            this.groupSize = 1;
            return;
        }
        let fullyVisibleItems = 0;
        this.items.forEach((item) => {
            const coords = item.getBoundingClientRect();
            // Count only items that are fully visible
            const itemStart = coords.x + MAGIC_THRESHOLD_PX;
            const itemEnd = coords.x + coords.width - MAGIC_THRESHOLD_PX;
            const isFullyVisible = itemStart >= this.edges.left && itemEnd <= this.edges.right;
            if (isFullyVisible) {
                fullyVisibleItems++;
            }
        });
        this.groupSize = fullyVisibleItems || 1;
    }
    groupItemsIndexes() {
        const itemsIndexes = this.items.map((_, index) => index + 1);
        const maxLength = Math.ceil(itemsIndexes.length / this.groupSize);
        this.groupedIndexes = Array.from({ length: maxLength }, (_, index) => {
            const start = index * this.groupSize;
            const end = start + this.groupSize;
            return itemsIndexes.slice(start, end);
        });
    }
    render() {
        const { WbDotNavigation, WbRoundButton, WbIcon } = this.TagNames;
        const hasLeftArrow = this.showArrowNavigation && !this.scrolledToStart;
        const hasRightArrow = this.showArrowNavigation && !this.scrolledToEnd;
        const hasDotNavigation = this.showDotNavigation && this.numberOfDots > 1;
        return (h(Host, { key: '543c31578a87d0bbad212196edfecaa24292ad13', class: "wb-slider", "data-on": getDataOnFromTheme(this.el, this.theme) }, h("div", { key: 'dd129ff94a3651ca2121a5f8450fbd9423a904b1', class: "grid-width", ref: this.bindGridWidthRef }), h("div", { key: '7b6269a7287b80411fe78c1cd3af7378d9220896', class: "outer" }, h("div", { key: '61a2555515d0acf909861e2ff8555ef0dc3660ed', class: "inner", ref: this.bindScrollerRef, onScroll: this.throttledUpdate, tabindex: "0" }, h("slot", { key: '725da7b1b16eb35ece9582b9cadba62aa1a06d8e', onSlotchange: this.updateItems })), hasLeftArrow && (h(WbRoundButton, { key: 'b0b96bd6c2705ef622ea0d9a583c1e9cbfe2f0f9', size: "new-l", elevation: "s", class: "arrow arrow--previous", "button-aria-label": this.previousButtonAriaLabel, onClick: this.onPreviousButtonClick }, h(WbIcon, { key: '0c2f610235b0884fd668c747569fd46c314b4405', name: "chevron-left/24", "flip-rtl": true }), this.previousButtonAriaLabel)), hasRightArrow && (h(WbRoundButton, { key: '43893715e5823dc7b796b5a7d92604932b03c3c5', size: "new-l", elevation: "s", class: "arrow arrow--next", "button-aria-label": this.nextButtonAriaLabel, onClick: this.onNextButtonClick }, h(WbIcon, { key: 'e329d1f44f52c82b67398c094800fd39f23d4c4e', name: "chevron-right/24", "flip-rtl": true }), this.nextButtonAriaLabel))), hasDotNavigation && (h(WbDotNavigation //
        , { key: '0e6b8e09e91ed5e81ffb7c2a1c3ad7651424be48', hasBackground: this.dotNavigationHasBackground, currentPage: this.getCurrentPage(), onWbchange: this.onDotNavigationChange, pages: this.numberOfDots, preventDotClickChange: true }))));
    }
    get el() { return getElement(this); }
    static get watchers() { return {
        "autoRotateIntervalMs": ["updateAutoRotation"],
        "initialItem": ["updateInitialItem"],
        "items": ["updateEdges"],
        "isLoaded": ["updateEdges"],
        "edges": ["groupItems"],
        "groupSize": ["groupItems"],
        "groupedIndexes": ["updateNumberOfDots"],
        "numberOfDots": ["updateCurrentItem"]
    }; }
};
SliderComponent.style = sliderCss;

const sliderItemCss = ":host{display:flex;flex-shrink:0;flex-direction:column}:host([mq1=\"1\"]){width:calc(var(--wb-grid-column-width) * 1 + var(--wb-grid-gutter-width) * 0)}:host([mq1=\"2\"]){width:calc(var(--wb-grid-column-width) * 2 + var(--wb-grid-gutter-width) * 1)}:host([mq1=\"3\"]){width:calc(var(--wb-grid-column-width) * 3 + var(--wb-grid-gutter-width) * 2)}:host([mq1=\"4\"]){width:calc(var(--wb-grid-column-width) * 4 + var(--wb-grid-gutter-width) * 3)}:host([mq1=\"5\"]){width:calc(var(--wb-grid-column-width) * 5 + var(--wb-grid-gutter-width) * 4)}:host([mq1=\"6\"]){width:calc(var(--wb-grid-column-width) * 6 + var(--wb-grid-gutter-width) * 5)}:host([mq1=\"7\"]){width:calc(var(--wb-grid-column-width) * 7 + var(--wb-grid-gutter-width) * 6)}:host([mq1=\"8\"]){width:calc(var(--wb-grid-column-width) * 8 + var(--wb-grid-gutter-width) * 7)}:host([mq1=\"9\"]){width:calc(var(--wb-grid-column-width) * 9 + var(--wb-grid-gutter-width) * 8)}:host([mq1=\"10\"]){width:calc(var(--wb-grid-column-width) * 10 + var(--wb-grid-gutter-width) * 9)}:host([mq1=\"11\"]){width:calc(var(--wb-grid-column-width) * 11 + var(--wb-grid-gutter-width) * 10)}:host([mq1=\"12\"]){width:calc(var(--wb-grid-column-width) * 12 + var(--wb-grid-gutter-width) * 11)}@media (min-width: 30rem){:host([mq2=\"1\"]){width:calc(var(--wb-grid-column-width) * 1 + var(--wb-grid-gutter-width) * 0)}:host([mq2=\"2\"]){width:calc(var(--wb-grid-column-width) * 2 + var(--wb-grid-gutter-width) * 1)}:host([mq2=\"3\"]){width:calc(var(--wb-grid-column-width) * 3 + var(--wb-grid-gutter-width) * 2)}:host([mq2=\"4\"]){width:calc(var(--wb-grid-column-width) * 4 + var(--wb-grid-gutter-width) * 3)}:host([mq2=\"5\"]){width:calc(var(--wb-grid-column-width) * 5 + var(--wb-grid-gutter-width) * 4)}:host([mq2=\"6\"]){width:calc(var(--wb-grid-column-width) * 6 + var(--wb-grid-gutter-width) * 5)}:host([mq2=\"7\"]){width:calc(var(--wb-grid-column-width) * 7 + var(--wb-grid-gutter-width) * 6)}:host([mq2=\"8\"]){width:calc(var(--wb-grid-column-width) * 8 + var(--wb-grid-gutter-width) * 7)}:host([mq2=\"9\"]){width:calc(var(--wb-grid-column-width) * 9 + var(--wb-grid-gutter-width) * 8)}:host([mq2=\"10\"]){width:calc(var(--wb-grid-column-width) * 10 + var(--wb-grid-gutter-width) * 9)}:host([mq2=\"11\"]){width:calc(var(--wb-grid-column-width) * 11 + var(--wb-grid-gutter-width) * 10)}:host([mq2=\"12\"]){width:calc(var(--wb-grid-column-width) * 12 + var(--wb-grid-gutter-width) * 11)}}@media (min-width: 48rem){:host([mq3=\"1\"]){width:calc(var(--wb-grid-column-width) * 1 + var(--wb-grid-gutter-width) * 0)}:host([mq3=\"2\"]){width:calc(var(--wb-grid-column-width) * 2 + var(--wb-grid-gutter-width) * 1)}:host([mq3=\"3\"]){width:calc(var(--wb-grid-column-width) * 3 + var(--wb-grid-gutter-width) * 2)}:host([mq3=\"4\"]){width:calc(var(--wb-grid-column-width) * 4 + var(--wb-grid-gutter-width) * 3)}:host([mq3=\"5\"]){width:calc(var(--wb-grid-column-width) * 5 + var(--wb-grid-gutter-width) * 4)}:host([mq3=\"6\"]){width:calc(var(--wb-grid-column-width) * 6 + var(--wb-grid-gutter-width) * 5)}:host([mq3=\"7\"]){width:calc(var(--wb-grid-column-width) * 7 + var(--wb-grid-gutter-width) * 6)}:host([mq3=\"8\"]){width:calc(var(--wb-grid-column-width) * 8 + var(--wb-grid-gutter-width) * 7)}:host([mq3=\"9\"]){width:calc(var(--wb-grid-column-width) * 9 + var(--wb-grid-gutter-width) * 8)}:host([mq3=\"10\"]){width:calc(var(--wb-grid-column-width) * 10 + var(--wb-grid-gutter-width) * 9)}:host([mq3=\"11\"]){width:calc(var(--wb-grid-column-width) * 11 + var(--wb-grid-gutter-width) * 10)}:host([mq3=\"12\"]){width:calc(var(--wb-grid-column-width) * 12 + var(--wb-grid-gutter-width) * 11)}}@media (min-width: 64rem){:host([mq4=\"1\"]){width:calc(var(--wb-grid-column-width) * 1 + var(--wb-grid-gutter-width) * 0)}:host([mq4=\"2\"]){width:calc(var(--wb-grid-column-width) * 2 + var(--wb-grid-gutter-width) * 1)}:host([mq4=\"3\"]){width:calc(var(--wb-grid-column-width) * 3 + var(--wb-grid-gutter-width) * 2)}:host([mq4=\"4\"]){width:calc(var(--wb-grid-column-width) * 4 + var(--wb-grid-gutter-width) * 3)}:host([mq4=\"5\"]){width:calc(var(--wb-grid-column-width) * 5 + var(--wb-grid-gutter-width) * 4)}:host([mq4=\"6\"]){width:calc(var(--wb-grid-column-width) * 6 + var(--wb-grid-gutter-width) * 5)}:host([mq4=\"7\"]){width:calc(var(--wb-grid-column-width) * 7 + var(--wb-grid-gutter-width) * 6)}:host([mq4=\"8\"]){width:calc(var(--wb-grid-column-width) * 8 + var(--wb-grid-gutter-width) * 7)}:host([mq4=\"9\"]){width:calc(var(--wb-grid-column-width) * 9 + var(--wb-grid-gutter-width) * 8)}:host([mq4=\"10\"]){width:calc(var(--wb-grid-column-width) * 10 + var(--wb-grid-gutter-width) * 9)}:host([mq4=\"11\"]){width:calc(var(--wb-grid-column-width) * 11 + var(--wb-grid-gutter-width) * 10)}:host([mq4=\"12\"]){width:calc(var(--wb-grid-column-width) * 12 + var(--wb-grid-gutter-width) * 11)}}@media (min-width: 80rem){:host([mq5=\"1\"]){width:calc(var(--wb-grid-column-width) * 1 + var(--wb-grid-gutter-width) * 0)}:host([mq5=\"2\"]){width:calc(var(--wb-grid-column-width) * 2 + var(--wb-grid-gutter-width) * 1)}:host([mq5=\"3\"]){width:calc(var(--wb-grid-column-width) * 3 + var(--wb-grid-gutter-width) * 2)}:host([mq5=\"4\"]){width:calc(var(--wb-grid-column-width) * 4 + var(--wb-grid-gutter-width) * 3)}:host([mq5=\"5\"]){width:calc(var(--wb-grid-column-width) * 5 + var(--wb-grid-gutter-width) * 4)}:host([mq5=\"6\"]){width:calc(var(--wb-grid-column-width) * 6 + var(--wb-grid-gutter-width) * 5)}:host([mq5=\"7\"]){width:calc(var(--wb-grid-column-width) * 7 + var(--wb-grid-gutter-width) * 6)}:host([mq5=\"8\"]){width:calc(var(--wb-grid-column-width) * 8 + var(--wb-grid-gutter-width) * 7)}:host([mq5=\"9\"]){width:calc(var(--wb-grid-column-width) * 9 + var(--wb-grid-gutter-width) * 8)}:host([mq5=\"10\"]){width:calc(var(--wb-grid-column-width) * 10 + var(--wb-grid-gutter-width) * 9)}:host([mq5=\"11\"]){width:calc(var(--wb-grid-column-width) * 11 + var(--wb-grid-gutter-width) * 10)}:host([mq5=\"12\"]){width:calc(var(--wb-grid-column-width) * 12 + var(--wb-grid-gutter-width) * 11)}}@media (min-width: 90rem){:host([mq6=\"1\"]){width:calc(var(--wb-grid-column-width) * 1 + var(--wb-grid-gutter-width) * 0)}:host([mq6=\"2\"]){width:calc(var(--wb-grid-column-width) * 2 + var(--wb-grid-gutter-width) * 1)}:host([mq6=\"3\"]){width:calc(var(--wb-grid-column-width) * 3 + var(--wb-grid-gutter-width) * 2)}:host([mq6=\"4\"]){width:calc(var(--wb-grid-column-width) * 4 + var(--wb-grid-gutter-width) * 3)}:host([mq6=\"5\"]){width:calc(var(--wb-grid-column-width) * 5 + var(--wb-grid-gutter-width) * 4)}:host([mq6=\"6\"]){width:calc(var(--wb-grid-column-width) * 6 + var(--wb-grid-gutter-width) * 5)}:host([mq6=\"7\"]){width:calc(var(--wb-grid-column-width) * 7 + var(--wb-grid-gutter-width) * 6)}:host([mq6=\"8\"]){width:calc(var(--wb-grid-column-width) * 8 + var(--wb-grid-gutter-width) * 7)}:host([mq6=\"9\"]){width:calc(var(--wb-grid-column-width) * 9 + var(--wb-grid-gutter-width) * 8)}:host([mq6=\"10\"]){width:calc(var(--wb-grid-column-width) * 10 + var(--wb-grid-gutter-width) * 9)}:host([mq6=\"11\"]){width:calc(var(--wb-grid-column-width) * 11 + var(--wb-grid-gutter-width) * 10)}:host([mq6=\"12\"]){width:calc(var(--wb-grid-column-width) * 12 + var(--wb-grid-gutter-width) * 11)}}";

const SliderItemComponent = class {
    constructor(hostRef) {
        registerInstance(this, hostRef);
    }
    render() {
        return (h(Host, { key: '6244b7c77c36e5cfeadc88856fffa646b06addde', class: "wb-slider-item" }, h("slot", { key: '43dfd7375be0707b92699d9097f1a9f3afb00221' })));
    }
    get el() { return getElement(this); }
};
SliderItemComponent.style = sliderItemCss;

export { SliderComponent as wb_slider, SliderItemComponent as wb_slider_item };
//# sourceMappingURL=wb-slider.wb-slider-item.entry.js.map
